import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { MineralType } from 'types/minerals';
import { XAxisType } from 'components/well-page-content/types';
import {
  useProductionVolumes,
  useTypeCurveVolumes,
  useAggregationVolumes,
} from '../hooks';
import type { FilteredWell } from '../types';
import {
  monthlyVolumesChartAtom,
  ratiosChartAtom,
  tcWellsLoadingAtom,
  tcHasTypeCurveAreaAtom,
} from '../type-curve-filters/atoms';
import {
  tcStreamAggregationAtom,
  tcProductionWellsAtom,
  tcTypeCurveVolumesAtom,
  tcAggregationCacheAtom,
  tcWellCountsAtom,
} from '../atoms';
import { tcDcaForecastVolumesAtom } from '../type-curve-dca/dca-fit-atoms';
import styles from './type-curve-volume-charts.module.scss';
import { MultiWellVolumeChart } from 'components/charts';
import { type MultiWellSeriesStyle } from 'components/charts/multi-well-volume-chart';
import { XAxisSwitcher } from 'components/well-page-content/decline-curve-analysis/x-axis-switcher';
import { Dropdown } from '@carbon/react';
import { Edit, Maximize } from '@carbon/react/icons';
import { ExpandableContainer } from 'components/expandable-container';

type ChartType = 'monthly' | 'logarithmic' | 'cumulative' | 'ratio';

const NON_RATIO_CHART_TYPES: Array<{ id: ChartType; text: string }> = [
  { id: 'monthly', text: 'Monthly Volumes' },
  { id: 'logarithmic', text: 'Log Log Volumes' },
  { id: 'cumulative', text: 'Cumulative Volumes' },
];

const CHART_TYPE_LABELS: Record<ChartType, string> = {
  monthly: 'Monthly Volumes',
  logarithmic: 'Log Log Volumes',
  cumulative: 'Cumulative Volumes',
  ratio: 'Ratio',
};

// Stream colors — kept in sync with STREAM_COLORS in volume-chart-2.tsx.
const STREAM_COLORS: Record<string, string> = {
  oil: '#42be65',
  gas: '#fa4d56',
  water: '#4589ff',
  gor: '#FF7EB6',
  wor: '#82CFFF',
  wgr: '#BE95FF',
  cgr: '#d2a106',
  watercut: '#0062ff',
};

// Stable selectedSeries arrays — defined at module level so references
// don't change between renders (prevents unnecessary chart rebuilds).
const NON_RATIO_STREAMS: Array<{
  id: MineralType;
  label: string;
  selectedSeries: MineralType[];
  color: string;
}> = [
  {
    id: 'oil',
    label: 'Oil',
    selectedSeries: ['oil'],
    color: STREAM_COLORS.oil,
  },
  {
    id: 'gas',
    label: 'Gas',
    selectedSeries: ['gas'],
    color: STREAM_COLORS.gas,
  },
  {
    id: 'water',
    label: 'Water',
    selectedSeries: ['water'],
    color: STREAM_COLORS.water,
  },
];

const RATIO_STREAMS: Array<{
  id: 'gor' | 'wor' | 'wgr' | 'cgr' | 'watercut';
  label: string;
  selectedSeries: Array<'gor' | 'wor' | 'wgr' | 'cgr' | 'watercut'>;
  color: string;
}> = [
  {
    id: 'gor',
    label: 'GOR',
    selectedSeries: ['gor'],
    color: STREAM_COLORS.gor,
  },
  {
    id: 'wor',
    label: 'WOR',
    selectedSeries: ['wor'],
    color: STREAM_COLORS.wor,
  },
  {
    id: 'wgr',
    label: 'WGR',
    selectedSeries: ['wgr'],
    color: STREAM_COLORS.wgr,
  },
  {
    id: 'cgr',
    label: 'CGR',
    selectedSeries: ['cgr'],
    color: STREAM_COLORS.cgr,
  },
  {
    id: 'watercut',
    label: 'Watercut',
    selectedSeries: ['watercut'],
    color: STREAM_COLORS.watercut,
  },
];

// Per-stream seriesStyle objects — defined at module level so they are stable
// references across renders and never trigger unnecessary chart rebuilds.
const STREAM_SERIES_STYLES: Record<string, MultiWellSeriesStyle> =
  Object.fromEntries(
    Object.entries(STREAM_COLORS).map(([id, color]) => [
      id,
      {
        aggregation: { color },
        forecast: { color: '#161616', style: 'dash' as const },
      },
    ])
  );

const AGGREGATION_OPTIONS: Array<{ id: string; text: string }> = [
  { id: 'p10', text: 'P10' },
  { id: 'p50', text: 'P50' },
  { id: 'p90', text: 'P90' },
  { id: 'arithmeticMean', text: 'Arithmetic Avg' },
  { id: 'geometricMean', text: 'Geometric Avg' },
];

interface AggregationDropdownProps {
  id: string;
  value: string;
  onChange: (type: string) => void;
  className?: string;
}

const AggregationDropdown: FC<AggregationDropdownProps> = ({
  id,
  value,
  onChange,
  className,
}) => (
  <Dropdown
    id={id}
    titleText=""
    size="sm"
    items={AGGREGATION_OPTIONS}
    itemToString={(item) => (item ? item.text : '')}
    label={AGGREGATION_OPTIONS.find((o) => o.id === value)?.text ?? 'Agg:'}
    selectedItem={AGGREGATION_OPTIONS.find((o) => o.id === value) ?? null}
    onChange={({ selectedItem }) => {
      if (selectedItem) onChange(selectedItem.id);
    }}
    className={className}
  />
);

interface TypeCurveVolumeChartsProps {
  wells: FilteredWell[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  /** UWI to highlight across all charts (controlled by the parent section). */
  highlightedUwi?: string | null;
  /** Fired when the user hovers over / leaves a well line (lifted to parent). */
  onWellHover?: (uwi: string | null) => void;
  /**
   * UWI → hex color map for color-by-attribute on monthly volume / log-log /
   * cumulative charts. Wells not in the map use the per-stream default color.
   */
  monthlyVolumesColorMap?: Record<string, string>;
  /** UWI → hex color map for color-by-attribute on ratio charts. */
  ratiosColorMap?: Record<string, string>;
  /** Fired when the user clicks the pencil/edit icon on a stream chart. */
  onEditStream?: (stream: MineralType) => void;
  /** Default aggregation type from typeWellDefaults — used as fallback instead of 'p50'. */
  defaultAggregation?: string;
}

export const TypeCurveVolumeCharts = ({
  wells,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  highlightedUwi,
  onWellHover,
  monthlyVolumesColorMap,
  ratiosColorMap,
  onEditStream,
  defaultAggregation,
}: TypeCurveVolumeChartsProps) => {
  // Per-stream chart type: streamId → ChartType. Defaults to 'monthly'.
  const [streamChartTypes, setStreamChartTypes] = useState<
    Record<string, ChartType>
  >(() =>
    NON_RATIO_STREAMS.reduce<Record<string, ChartType>>(
      (acc, s) => ({ ...acc, [s.id]: 'monthly' }),
      {}
    )
  );

  // Per-stream x-axis type: streamId → XAxisType. Defaults to 'count'.
  const [streamXAxisTypes, setStreamXAxisTypes] = useState<
    Record<string, XAxisType>
  >(() =>
    [...NON_RATIO_STREAMS, ...RATIO_STREAMS].reduce<Record<string, XAxisType>>(
      (acc, s) => ({ ...acc, [s.id]: 'count' }),
      {}
    )
  );

  // Per-stream aggregation selection — shared with the DCA modal via atom.
  const [streamAggTypes, setStreamAggTypes] = useAtom(tcStreamAggregationAtom);
  // Derived from streamAggTypes so the hook always requests exactly the types
  // currently selected in the UI — no accumulated stale types. When filteredUwis
  // changes the hook resets its internal cache and re-fetches only these types.
  const requestedAggTypes = useMemo(
    () => [
      ...new Set(
        [...NON_RATIO_STREAMS, ...RATIO_STREAMS]
          .map((s) => streamAggTypes[s.id] ?? (defaultAggregation || 'p50'))
          .filter((t) => t !== 'none')
      ),
    ],
    [streamAggTypes, defaultAggregation]
  );

  const handleAggChange = useCallback(
    (streamId: string, type: string) => {
      setStreamAggTypes((prev) => ({ ...prev, [streamId]: type }));
    },
    [setStreamAggTypes]
  );

  const handleChartTypeChange = useCallback(
    (streamId: string, chartType: ChartType) => {
      setStreamChartTypes((prev) => ({ ...prev, [streamId]: chartType }));
      // Logarithmic mode does not support date x-axis — reset for this stream only.
      if (chartType === 'logarithmic') {
        setStreamXAxisTypes((prev) => {
          if (prev[streamId] === 'date') {
            return { ...prev, [streamId]: 'count' };
          }
          return prev;
        });
      }
    },
    []
  );

  const handleXAxisChange = useCallback(
    (streamId: string, xAxis: XAxisType) => {
      setStreamXAxisTypes((prev) => ({ ...prev, [streamId]: xAxis }));
    },
    []
  );

  const filteredUwis = useMemo(() => (wells ?? []).map((w) => w.uwi), [wells]);

  const { wells: productionWells, isLoading: productionLoading } =
    useProductionVolumes({
      filteredUwis: wells ?? [],
      typeCurveHeaderDetailsId,
      dcaScenarioId,
    });

  const hasTypeCurveArea = useAtomValue(tcHasTypeCurveAreaAtom) !== false;
  const { typeCurveData } = useTypeCurveVolumes({
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    hasTypeCurveArea,
  });

  // Single hook call — fetches all requested types in one request, caches by type.
  // wellCounts is returned alongside and is independent of the aggregation type.
  const { aggregationCache, wellCounts } = useAggregationVolumes({
    filteredUwis,
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    requestedTypes: requestedAggTypes,
  });

  // ── Sync fetched data into shared atoms for the DCA modal ──
  const setSharedProductionWells = useSetAtom(tcProductionWellsAtom);
  const setSharedTypeCurveVolumes = useSetAtom(tcTypeCurveVolumesAtom);
  const setSharedAggregationCache = useSetAtom(tcAggregationCacheAtom);
  const setSharedWellCounts = useSetAtom(tcWellCountsAtom);

  useEffect(() => {
    setSharedProductionWells(productionWells);
  }, [productionWells, setSharedProductionWells]);
  useEffect(() => {
    setSharedTypeCurveVolumes(typeCurveData);
  }, [typeCurveData, setSharedTypeCurveVolumes]);
  useEffect(() => {
    setSharedAggregationCache(aggregationCache);
  }, [aggregationCache, setSharedAggregationCache]);
  useEffect(() => {
    setSharedWellCounts(wellCounts);
  }, [wellCounts, setSharedWellCounts]);

  const wellsLoading = useAtomValue(tcWellsLoadingAtom);
  const isLoading = productionLoading || wellsLoading;

  const dcaForecastVolumes = useAtomValue(tcDcaForecastVolumesAtom);

  const monthlyVisibility = useAtomValue(monthlyVolumesChartAtom);
  const ratiosVisibility = useAtomValue(ratiosChartAtom);

  const visibleNonRatioStreams = NON_RATIO_STREAMS.filter(
    (s) => monthlyVisibility[s.id as keyof typeof monthlyVisibility]
  );
  const visibleRatioStreams = RATIO_STREAMS.filter(
    (s) => ratiosVisibility[s.id as keyof typeof ratiosVisibility]
  );

  return (
    <>
      {/* Non-ratio volume charts — visibility controlled by monthlyVolumesChartAtom */}
      {visibleNonRatioStreams.map((stream) => {
        const chartType = streamChartTypes[stream.id] ?? 'monthly';
        const xAxisType = streamXAxisTypes[stream.id] ?? 'count';
        const aggType =
          streamAggTypes[stream.id] ?? (defaultAggregation || 'p50');
        const aggData =
          aggType !== 'none' ? (aggregationCache[aggType] ?? null) : null;

        // Default zoom: set xLinearMax to 180 for 'count' x-axis to show series properly
        const xLinearMax =
          xAxisType === 'count' && chartType !== 'logarithmic'
            ? 180
            : undefined;

        return (
          <ExpandableContainer key={stream.id}>
            {({ isExpanded, expand }) => (
              <MultiWellVolumeChart
                fillHeight
                type={chartType}
                wells={productionWells}
                typeCurveData={typeCurveData}
                showTypeCurve={!!typeCurveData}
                aggregationData={aggData}
                showAggregation={aggType !== 'none' && !!aggData}
                aggregationLabel={
                  AGGREGATION_OPTIONS.find((o) => o.id === aggType)?.text
                }
                wellCountData={wellCounts}
                showWellCount={!!wellCounts}
                forecastData={dcaForecastVolumes}
                showForecast={!!dcaForecastVolumes}
                isLoading={isLoading}
                selectedSeries={stream.selectedSeries}
                highlightedUwi={highlightedUwi}
                onWellHover={onWellHover}
                xAxisType={xAxisType}
                xLinearMax={xLinearMax}
                autoZoomY
                seriesStyle={STREAM_SERIES_STYLES[stream.id]}
                uwiColorMap={monthlyVolumesColorMap}
                exportConfig={
                  // No export trigger in the expanded view — it would collide
                  // with the expand overlay's close button.
                  isExpanded
                    ? undefined
                    : {
                        label: `${CHART_TYPE_LABELS[chartType]} — ${stream.label}`,
                        filenamePrefix: `type-curve-${chartType}-${stream.id}`,
                      }
                }
                header={
                  <div className={styles.chartTitleRow}>
                    <span className={styles.chartTitle}>
                      {CHART_TYPE_LABELS[chartType]} &mdash; {stream.label}
                    </span>
                    <div className={styles.chartHeaderActions}>
                      {onEditStream && !isExpanded && (
                        <button
                          type="button"
                          className={styles.chartEditButton}
                          onClick={() => onEditStream(stream.id)}
                          disabled={isLoading}
                          aria-label={`Edit ${stream.label} DCA`}
                        >
                          <Edit size={16} />
                        </button>
                      )}
                      {!isExpanded && (
                        <button
                          type="button"
                          className={styles.chartExpandButton}
                          onClick={expand}
                          aria-label={`Expand ${stream.label} chart`}
                        >
                          <Maximize size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                }
                footer={
                  <div className={styles.volumeFooter}>
                    <div className={styles.footerGroup}>
                      <span className={styles.footerLabel}>X-axis:</span>
                      <XAxisSwitcher
                        chartType={chartType}
                        value={xAxisType}
                        onChange={(val) => handleXAxisChange(stream.id, val)}
                        className={styles.footerDropdown}
                        excludeOptions={['date']}
                      />
                    </div>
                    <div className={styles.footerGroup}>
                      <span className={styles.footerLabel}>Chart type:</span>
                      <Dropdown
                        id={`mw-chart-type-${stream.id}`}
                        titleText=""
                        size="sm"
                        items={NON_RATIO_CHART_TYPES}
                        itemToString={(item) => (item ? item.text : '')}
                        label={
                          NON_RATIO_CHART_TYPES.find((t) => t.id === chartType)
                            ?.text ?? 'Chart Type'
                        }
                        selectedItem={NON_RATIO_CHART_TYPES.find(
                          (t) => t.id === chartType
                        )}
                        onChange={({ selectedItem }) => {
                          if (selectedItem)
                            handleChartTypeChange(stream.id, selectedItem.id);
                        }}
                        className={styles.footerDropdown}
                      />
                    </div>
                    <div className={styles.footerGroup}>
                      <span className={styles.footerLabel}>Agg:</span>
                      <AggregationDropdown
                        id={`mw-agg-${stream.id}`}
                        value={aggType}
                        onChange={(type) => handleAggChange(stream.id, type)}
                        className={styles.footerDropdown}
                      />
                    </div>
                  </div>
                }
              />
            )}
          </ExpandableContainer>
        );
      })}

      {/* Ratio charts — visibility controlled by ratiosChartAtom */}
      {visibleRatioStreams.map((stream) => {
        const xAxisType = streamXAxisTypes[stream.id] ?? 'count';
        const aggType =
          streamAggTypes[stream.id] ?? (defaultAggregation || 'p50');
        const aggData =
          aggType !== 'none' ? (aggregationCache[aggType] ?? null) : null;

        // Default zoom: ensure ratio charts on the count axis show at
        // least 180 months by default, even when data extent is shorter.
        // autoZoomX still extends the range if data goes beyond 180.
        const xLinearMax = xAxisType === 'count' ? 180 : undefined;

        return (
          <ExpandableContainer key={stream.id}>
            {({ isExpanded, expand }) => (
              <MultiWellVolumeChart
                fillHeight
                type="ratio"
                wells={productionWells}
                typeCurveData={typeCurveData}
                showTypeCurve={!!typeCurveData}
                aggregationData={aggData}
                showAggregation={aggType !== 'none' && !!aggData}
                aggregationLabel={
                  AGGREGATION_OPTIONS.find((o) => o.id === aggType)?.text
                }
                wellCountData={wellCounts}
                showWellCount={!!wellCounts}
                forecastData={dcaForecastVolumes}
                showForecast={!!dcaForecastVolumes}
                isLoading={isLoading}
                selectedSeries={stream.selectedSeries}
                highlightedUwi={highlightedUwi}
                onWellHover={onWellHover}
                xAxisType={xAxisType}
                xLinearMax={xLinearMax}
                autoZoomX
                autoZoomY
                seriesStyle={STREAM_SERIES_STYLES[stream.id]}
                uwiColorMap={ratiosColorMap}
                exportConfig={
                  // No export trigger in the expanded view — it would collide
                  // with the expand overlay's close button.
                  isExpanded
                    ? undefined
                    : {
                        label: stream.label,
                        filenamePrefix: `type-curve-ratio-${stream.id}`,
                      }
                }
                header={
                  <div className={styles.chartTitleRow}>
                    <span className={styles.chartTitle}>{stream.label}</span>
                    <div className={styles.chartHeaderActions}>
                      {!isExpanded && (
                        <button
                          type="button"
                          className={styles.chartExpandButton}
                          onClick={expand}
                          aria-label={`Expand ${stream.label} chart`}
                        >
                          <Maximize size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                }
                footer={
                  <div className={styles.volumeFooter}>
                    <div className={styles.footerGroup}>
                      <span className={styles.footerLabel}>X-axis:</span>
                      <XAxisSwitcher
                        chartType="ratio"
                        value={xAxisType}
                        onChange={(val) => handleXAxisChange(stream.id, val)}
                        className={styles.footerDropdown}
                        excludeOptions={['date']}
                      />
                    </div>
                    <div className={styles.footerGroup}>
                      <span className={styles.footerLabel}>Agg:</span>
                      <AggregationDropdown
                        id={`mw-agg-ratio-${stream.id}`}
                        value={aggType}
                        onChange={(type) => handleAggChange(stream.id, type)}
                        className={styles.footerDropdown}
                      />
                    </div>
                  </div>
                }
              />
            )}
          </ExpandableContainer>
        );
      })}
    </>
  );
};
